import { useSyncExternalStore } from 'react';
import type { MaintenanceNoticeEvent } from '@atlas/shared';

/**
 * "The stack is about to restart" — pushed over the socket by the deploy
 * (`scripts/remote-deploy.sh` → `POST /internal/maintenance-notice`) a few
 * seconds before the containers are swapped.
 *
 * A plain module store rather than a context, for the same reason as
 * `previewRole`: one value, read by one banner, written by one socket handler.
 * In memory only — a reload after the restart should come back to a clean app,
 * not to a stale banner about an update that already finished.
 */
let notice: MaintenanceNoticeEvent | null = null;
const listeners = new Set<() => void>();

/**
 * Window event asking every open form that can save itself to do so NOW,
 * rather than waiting out its autosave debounce. The DOM is the bus so that
 * `EvaluationContext` can listen without the socket layer importing it.
 */
export const SAVE_NOW_EVENT = 'atlas:save-now';

/**
 * How long after a notice we give up waiting for the restart to happen.
 *
 * The banner is a promise that the site is about to go away. If it does not —
 * a deploy that aborted after sending the notice, or a test notice like the one
 * fired on 2026-09-04 — the promise has to expire, or the banner sits there
 * being wrong until the reader happens to reload.
 */
const GRACE_MS = 120_000;

export function hasNoticeExpired(
  notice: MaintenanceNoticeEvent,
  now: number = Date.now(),
): boolean {
  const due = new Date(notice.startedAt).getTime() + notice.leadSeconds * 1000 + GRACE_MS;
  return now > due;
}

export function setMaintenanceNotice(next: MaintenanceNoticeEvent | null): void {
  notice = next;
  listeners.forEach((n) => n());
  if (next) window.dispatchEvent(new Event(SAVE_NOW_EVENT));
}

function subscribe(notify: () => void): () => void {
  listeners.add(notify);
  return () => listeners.delete(notify);
}

export function useMaintenanceNotice(): MaintenanceNoticeEvent | null {
  return useSyncExternalStore(subscribe, () => notice, () => notice);
}
